import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, CircularProgress, Tooltip, IconButton, TextField,
  MenuItem, Grid,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '@components/layout/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { toastService } from '@services/toast.service.ts';
import { adminAiCacheService, AiFoodCacheItem } from '@services/admin-ai-cache.service.ts';
import { useDebounce } from '@hooks/useDebounce.ts';

const FOOD_TYPE_LABELS: Record<string, string> = {
  food: 'Їжа',
  liquid: 'Напій',
  unrecognized: 'Нерозпізнано',
};

const SOURCE_COLOR: Record<string, 'default' | 'primary' | 'warning'> = {
  ai: 'primary',
  manual: 'warning',
};

// ---------- Detail modal ----------

function FoodCacheDetailModal({ item, open, onClose }: {
  item: AiFoodCacheItem | null;
  open: boolean;
  onClose: () => void;
}) {
  if (!item) return null;

  const rows = [
    { label: 'ID', value: item.id },
    { label: 'Ключ', value: item.nameKey },
    { label: 'Назва', value: item.displayName },
    { label: 'Тип', value: item.foodType ? FOOD_TYPE_LABELS[item.foodType] : '—' },
    { label: 'Одиниця', value: item.unit ?? '—' },
    { label: 'Назва порції', value: item.portionName ?? '—' },
    { label: 'Розмір порції', value: item.portionSize != null ? `${item.portionSize} ${item.unit ?? ''}` : '—' },
    { label: 'Калорії / 100', value: item.caloriesPer100 != null ? `${item.caloriesPer100} ккал` : '—' },
    { label: 'Білки / 100', value: item.proteinPer100 != null ? `${item.proteinPer100} г` : '—' },
    { label: 'Жири / 100', value: item.fatPer100 != null ? `${item.fatPer100} г` : '—' },
    { label: 'Вуглеводи / 100', value: item.carbsPer100 != null ? `${item.carbsPer100} г` : '—' },
    { label: 'Клітковина / 100', value: item.fiberPer100 != null ? `${item.fiberPer100} г` : '—' },
    { label: 'Джерело', value: item.source === 'ai' ? 'AI' : 'Вручну' },
    { label: 'Звернень', value: item.hitCount },
    { label: 'Створено', value: new Date(item.createdAt).toLocaleString('uk') },
    { label: 'Оновлено', value: new Date(item.updatedAt).toLocaleString('uk') },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Деталі: {item.displayName}</DialogTitle>
      <DialogContent dividers>
        <Table size="small">
          <TableBody>
            {rows.map(({ label, value }) => (
              <TableRow key={label}>
                <TableCell sx={{ color: 'text.secondary', width: 160 }}>{label}</TableCell>
                <TableCell>{String(value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрити</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------- Edit modal ----------

type EditForm = {
  displayName: string;
  foodType: string;
  unit: string;
  portionName: string;
  portionSize: string;
  caloriesPer100: string;
  proteinPer100: string;
  fatPer100: string;
  carbsPer100: string;
  fiberPer100: string;
  source: string;
};

function toEditForm(item: AiFoodCacheItem): EditForm {
  return {
    displayName: item.displayName,
    foodType: item.foodType ?? '',
    unit: item.unit ?? '',
    portionName: item.portionName ?? '',
    portionSize: item.portionSize != null ? String(item.portionSize) : '',
    caloriesPer100: item.caloriesPer100 != null ? String(item.caloriesPer100) : '',
    proteinPer100: item.proteinPer100 != null ? String(item.proteinPer100) : '',
    fatPer100: item.fatPer100 != null ? String(item.fatPer100) : '',
    carbsPer100: item.carbsPer100 != null ? String(item.carbsPer100) : '',
    fiberPer100: item.fiberPer100 != null ? String(item.fiberPer100) : '',
    source: item.source,
  };
}

function parseNullableFloat(v: string): number | null {
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
}

function FoodCacheEditModal({ item, open, onClose, onSaved }: {
  item: AiFoodCacheItem | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: AiFoodCacheItem) => void;
}) {
  const [form, setForm] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) setForm(toEditForm(item));
  }, [item]);

  if (!item || !form) return null;

  const set = (field: keyof EditForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => f ? { ...f, [field]: e.target.value } : f);

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload: Partial<AiFoodCacheItem> = {
        displayName: form.displayName || undefined,
        foodType: (form.foodType as AiFoodCacheItem['foodType']) || null,
        unit: (form.unit as AiFoodCacheItem['unit']) || null,
        portionName: form.portionName || null,
        portionSize: parseNullableFloat(form.portionSize),
        caloriesPer100: parseNullableFloat(form.caloriesPer100),
        proteinPer100: parseNullableFloat(form.proteinPer100),
        fatPer100: parseNullableFloat(form.fatPer100),
        carbsPer100: parseNullableFloat(form.carbsPer100),
        fiberPer100: parseNullableFloat(form.fiberPer100),
        source: form.source as AiFoodCacheItem['source'],
      };
      const updated = await adminAiCacheService.updateFoodCache(item.id, payload);
      toastService.success('Збережено');
      onSaved(updated);
      onClose();
    } catch (error: any) {
      toastService.error(error?.message || 'Не вдалося зберегти');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Редагування: {item.displayName}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label="Назва"
              fullWidth
              value={form.displayName}
              onChange={set('displayName')}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              select
              label="Тип"
              fullWidth
              value={form.foodType}
              onChange={set('foodType')}
            >
              <MenuItem value="">—</MenuItem>
              <MenuItem value="food">Їжа</MenuItem>
              <MenuItem value="liquid">Напій</MenuItem>
              <MenuItem value="unrecognized">Нерозпізнано</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6}>
            <TextField
              select
              label="Одиниця"
              fullWidth
              value={form.unit}
              onChange={set('unit')}
            >
              <MenuItem value="">—</MenuItem>
              <MenuItem value="г">г</MenuItem>
              <MenuItem value="мл">мл</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Назва порції"
              fullWidth
              value={form.portionName}
              onChange={set('portionName')}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Розмір порції"
              fullWidth
              type="number"
              value={form.portionSize}
              onChange={set('portionSize')}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">На 100 г / мл</Typography>
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Калорії"
              fullWidth
              type="number"
              value={form.caloriesPer100}
              onChange={set('caloriesPer100')}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Білки"
              fullWidth
              type="number"
              value={form.proteinPer100}
              onChange={set('proteinPer100')}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Жири"
              fullWidth
              type="number"
              value={form.fatPer100}
              onChange={set('fatPer100')}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Вуглеводи"
              fullWidth
              type="number"
              value={form.carbsPer100}
              onChange={set('carbsPer100')}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Клітковина"
              fullWidth
              type="number"
              value={form.fiberPer100}
              onChange={set('fiberPer100')}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              select
              label="Джерело"
              fullWidth
              value={form.source}
              onChange={set('source')}
            >
              <MenuItem value="ai">AI</MenuItem>
              <MenuItem value="manual">Вручну</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Скасувати</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Збереження…' : 'Зберегти'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------- Page ----------

const AdminAiFoodCachePageContent: React.FC = () => {
  const [items, setItems] = useState<AiFoodCacheItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('');
  const [detail, setDetail] = useState<AiFoodCacheItem | null>(null);
  const [editing, setEditing] = useState<AiFoodCacheItem | null>(null);
  const debouncedSearch = useDebounce(search, 400);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAiCacheService.getFoodCache({
        page, limit,
        search: debouncedSearch || undefined,
        source: source as any || undefined,
      });
      setItems(res.data);
      setTotal(res.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити кеш їжі.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch, source]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleReset = () => {
    setSearch('');
    setSource('');
    setPage(1);
  };

  const handleSaved = (updated: AiFoodCacheItem) => {
    setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
  };

  return (
    <Box data-i-doc={PageIndices.ADMIN_AI_FOOD}>
      <PageHint iDoc={PageIndices.ADMIN_AI_FOOD} />

      <PageCard>
        <PageToolbar
          storageKey="filters:admin-ai-food-cache"
          filters={[
            {
              id: 'search',
              type: 'text',
              label: 'Пошук за назвою',
              value: search,
              onChange: (v) => { setSearch(v); setPage(1); },
              flexGrow: 1,
            },
            {
              id: 'source',
              type: 'select',
              label: 'Джерело',
              value: source,
              onChange: (v) => { setSource(v); setPage(1); },
              options: [
                { value: '', label: 'Всі' },
                { value: 'ai', label: 'AI' },
                { value: 'manual', label: 'Вручну' },
              ],
            },
          ]}
          onReset={handleReset}
          showReset
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            Записів не знайдено
          </Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Назва</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>Калорії / 100</TableCell>
                  <TableCell>Б / Ж / В</TableCell>
                  <TableCell>Порція</TableCell>
                  <TableCell align="center">Звернень</TableCell>
                  <TableCell align="center">Джерело</TableCell>
                  <TableCell align="center" />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setDetail(item)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{item.displayName}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.nameKey}</Typography>
                    </TableCell>
                    <TableCell>
                      {item.foodType ? FOOD_TYPE_LABELS[item.foodType] : '—'}
                    </TableCell>
                    <TableCell>
                      {item.caloriesPer100 != null ? `${item.caloriesPer100} ккал` : '—'}
                    </TableCell>
                    <TableCell>
                      {item.proteinPer100 != null
                        ? `${item.proteinPer100} / ${item.fatPer100} / ${item.carbsPer100}`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {item.portionName && item.portionSize
                        ? `${item.portionName} (${item.portionSize} ${item.unit ?? ''})`
                        : '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={item.hitCount} size="small" color={item.hitCount > 0 ? 'success' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={item.source === 'ai' ? 'AI' : 'Вручну'} size="small" color={SOURCE_COLOR[item.source]} />
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Детальніше">
                        <IconButton size="small" onClick={() => setDetail(item)}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Редагувати">
                        <IconButton size="small" onClick={() => setEditing(item)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <UniversalPagination
          page={page}
          count={Math.ceil(total / limit)}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(v) => { setLimit(v); setPage(1); }}
          storageKey="pagination:admin-ai-food-cache"
        />
      </PageCard>

      <FoodCacheDetailModal
        item={detail}
        open={!!detail}
        onClose={() => setDetail(null)}
      />

      <FoodCacheEditModal
        item={editing}
        open={!!editing}
        onClose={() => setEditing(null)}
        onSaved={handleSaved}
      />
    </Box>
  );
};

export const AdminAiFoodCachePage: React.FC = () => (
  <DashboardLayout>
    <AdminAiFoodCachePageContent />
  </DashboardLayout>
);
